import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from 'src/environments/environment';
import {
  WalletBalance,
  WalletEntryFilters,
  WalletEntryPage,
  WalletError,
  WalletPrices,
  WalletBilling,
  WalletHold,
  WalletHoldFilters,
  WalletUsageSummary,
} from 'src/app/models/Wallet';

/** Filtros que viram query string. `limit`, `cursor` e `include` entram separados. */
const ENTRY_FILTER_KEYS: Array<keyof WalletEntryFilters> = ['service', 'entry_type', 'from', 'to'];

@Injectable({ providedIn: 'root' })
export class WalletService {

  constructor(private http: HttpClient) {}

  getBalance(): Observable<WalletBalance> {
    return this.http.get<WalletBalance>(`${environment.apiUrl}/wallet/balance`);
  }

  /**
   * Preço vigente por produto (CU-86akn0zp6).
   *
   * Requisição própria, e não um campo do saldo: o preço muda por contrato e por
   * assinatura, não por movimentação, e pendurá-lo no `/wallet/balance` faria
   * toda consulta de saldo carregar a cascata de preço junto.
   *
   * O 503 desta rota é o mesmo `unavailable` do resto da carteira — quem chama
   * classifica com `classifyWalletError`.
   */
  getPrices(): Observable<WalletPrices> {
    return this.http.get<WalletPrices>(`${environment.apiUrl}/wallet/prices`);
  }

  /**
   * De onde sai cada canal desta conta. Não passa pela carteira: responde mesmo
   * com ela fora, que é quando a home mais precisa saber quais saldos de pacote
   * mostrar.
   */
  getBilling(): Observable<WalletBilling> {
    return this.http.get<WalletBilling>(`${environment.apiUrl}/wallet/billing`);
  }

  /** Reservas da carteira (07/10/2026). Ver `WalletHold`. */
  listHolds(filters: WalletHoldFilters = {}): Observable<{ data: WalletHold[] }> {
    let params = new HttpParams();

    (['status', 'service', 'from', 'to', 'limit'] as Array<keyof WalletHoldFilters>).forEach((key) => {
      const value = filters[key];

      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });

    return this.http.get<{ data: WalletHold[] }>(`${environment.apiUrl}/wallet/holds`, { params });
  }

  listEntries(filters: WalletEntryFilters = {}): Observable<WalletEntryPage> {
    let params = new HttpParams();

    if (filters.limit) {
      params = params.set('limit', String(filters.limit));
    }

    if (filters.cursor) {
      params = params.set('cursor', filters.cursor);
    }

    if (filters.include) {
      params = params.set('include', filters.include);
    }

    ENTRY_FILTER_KEYS.forEach((key) => {
      const value = filters[key];

      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });

    return this.http.get<WalletEntryPage>(`${environment.apiUrl}/wallet/entries`, { params });
  }

  /**
   * Consumo do período somado no servidor (17tm466t0ca), por dia e por canal.
   *
   * É o que o Dashboard usa no lugar de varrer o extrato: a soma vem pronta da
   * carteira (um `GROUP BY`, sem paginação), então não existe teto de lançamentos aqui.
   * `from`/`to` são datas civis (`yyyy-mm-dd`), inclusivas, em America/Sao_Paulo.
   */
  getUsageSummary(from: string, to: string): Observable<WalletUsageSummary> {
    const params = new HttpParams().set('from', from).set('to', to);

    return this.http.get<WalletUsageSummary>(`${environment.apiUrl}/wallet/usage-summary`, { params });
  }
}

/**
 * Traduz a falha HTTP nos três desfechos do contrato.
 *
 * Fica fora da classe porque quem trata o erro (modal do header e tela do
 * extrato) já tem a resposta na mão e não precisa do cliente HTTP injetado para
 * classificá-la.
 */
export function classifyWalletError(error: HttpErrorResponse): WalletError {
  const body = (error && error.error) || {};

  if (error && error.status === 403 && body.error === 'wallet_disabled') {
    return {
      kind: 'disabled',
      message: body.message || 'A carteira em R$ ainda não está habilitada para esta conta.',
      retryAfterSeconds: null,
    };
  }

  if (error && error.status === 503) {
    return {
      kind: 'unavailable',
      message: body.message || 'Não foi possível consultar a carteira agora. Tente novamente em instantes.',
      retryAfterSeconds: readRetryAfter(error),
    };
  }

  if (error && error.status === 422) {
    return {
      kind: 'invalid_filter',
      message: firstValidationMessage(body) || 'Filtro inválido.',
      retryAfterSeconds: null,
    };
  }

  return {
    kind: 'unknown',
    message: body.message || 'Não foi possível consultar a carteira agora.',
    retryAfterSeconds: null,
  };
}

/**
 * O header só chega ao JavaScript se o backend o expuser em
 * `Access-Control-Expose-Headers` — em domínio cruzado ele volta `null`. Por isso
 * o valor é opcional na UI: sem ele o botão de tentar de novo continua valendo,
 * só não anuncia o tempo.
 */
function readRetryAfter(error: HttpErrorResponse): number | null {
  const raw = error.headers ? error.headers.get('Retry-After') : null;

  if (!raw) {
    return null;
  }

  const seconds = Number(raw);

  return isFinite(seconds) && seconds > 0 ? Math.round(seconds) : null;
}

/** Laravel devolve `errors: { campo: [mensagens] }` no 422. */
function firstValidationMessage(body: any): string | null {
  const errors = body && body.errors;

  if (!errors) {
    return body && body.message ? body.message : null;
  }

  const firstField = Object.keys(errors)[0];

  if (!firstField) {
    return body.message || null;
  }

  const messages = errors[firstField];

  return Array.isArray(messages) ? messages[0] : String(messages);
}
